import { useMemo } from 'react';

import type { TableColumnsType } from 'antd';

import { useColumnVisibility } from '@/shared/ui/ColumnVisibilityButton';

type ColumnKey = 'name' | 'displayName' | 'description';

const DEFAULT_COLUMN_ORDER: ColumnKey[] = ['name', 'displayName', 'description'];
const DEFAULT_VISIBLE_COLUMNS: ColumnKey[] = ['name', 'displayName', 'description'];

type TableRowData = {
  key: string;
  id: string;
  name: string;
  displayName: string;
  description: string;
  fullyQualifiedName: string;
  provider: string;
};

type UseVisibleColumnProps = {
  columns: TableColumnsType<TableRowData>;
};

export const useTagCategoryVisibleColumn = ({ columns }: UseVisibleColumnProps) => {
  const allColumnsMap = useMemo(
    () =>
      columns.reduce(
        (acc, col) => {
          if (col.key) {
            acc[col.key as string] = { title: col.title as string };
          }
          return acc;
        },
        {} as Record<string, { title: string }>
      ),
    [columns]
  );

  const { state } = useColumnVisibility({
    defaultColumnOrder: DEFAULT_COLUMN_ORDER,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    allColumnsMap,
  });
  const { visibleColumns } = state;

  const isVisibleColumns = useMemo(() => {
    return visibleColumns.length > 0;
  }, [visibleColumns]);

  const filterColumns = useMemo<TableColumnsType<TableRowData>>(() => {
    return columns.filter((column) => visibleColumns.includes(column.key as ColumnKey));
  }, [columns, visibleColumns]);

  return {
    columns: filterColumns,
    isVisibleColumns,
    defaultColumnOrder: DEFAULT_COLUMN_ORDER,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  };
};
